import React, { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useLocation, useNavigate } from 'react-router-dom'
import ActionFeedbackAlert, { ACTION_FEEDBACK_DURATION_MS } from '../../components/alerts/ActionFeedbackAlert'
import LoadingSpinner from '../../components/LoadingSpinner'
import LeftTabsMui from '../../layout/LeftTabsMui'
import { layoutMobileMediaQuery } from '../../layout/layoutConstants'
import {
  MY_PROFILE_LOADING_TEXT,
  MY_PROFILE_PAGE_TITLE,
  MY_PROFILE_SECTION_TITLE,
  MY_PROFILE_TABS,
  myProfileLayoutSx,
  myProfilePanelLoaderSx
} from './constants/myProfileAppConstants'
import {
  buildFallbackProfile,
  buildMyProfileActionAlert,
  getMyProfileActiveTab
} from './helpers/myProfileAppHelpers'
import ProfileInfoSection from './profile/ProfileInfoSection'
import ProfileSecuritySection from './security/ProfileSecuritySection'
import { fetchMyProfile } from './services/profileApi'

export default function MyProfileApp() {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery)
  const [profile, setProfile] = useState(buildFallbackProfile)
  const [loading, setLoading] = useState(true)
  const [actionAlert, setActionAlert] = useState(null)

  const activeTab = useMemo(() => getMyProfileActiveTab(location.pathname), [location.pathname])

  useEffect(() => {
    if (!actionAlert) return undefined

    const dismissTimer = window.setTimeout(() => {
      setActionAlert(null)
    }, ACTION_FEEDBACK_DURATION_MS)

    return () => window.clearTimeout(dismissTimer)
  }, [actionAlert])

  const showActionAlert = (message, severity = 'success') => {
    setActionAlert(buildMyProfileActionAlert(message, severity))
  }

  useEffect(() => {
    setLoading(true)
    fetchMyProfile()
      .then(setProfile)
      .catch(() => setProfile(buildFallbackProfile()))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="my-profile-page">
      <div
        className="index-header detailed-container pos-task-header"
        style={{
          marginBottom: isMobileLayout ? (actionAlert ? 16 : undefined) : undefined
        }}
      >
        <h2>{MY_PROFILE_PAGE_TITLE}</h2>
        {actionAlert ? (
          <div className="index-buttons pos-create-action-wrap">
            <ActionFeedbackAlert
              alert={actionAlert}
              onClose={() => setActionAlert(null)}
              isMobileLayout={isMobileLayout}
              sx={{
                width: { xs: '100%', md: 'fit-content' },
                mt: 0
              }}
            />
          </div>
        ) : null}
      </div>

      <div className="index-table pos-task-index-table">
        <div className="show-area pos-task-show-area">
          <Box sx={myProfileLayoutSx}>
            <LeftTabsMui
              title={MY_PROFILE_SECTION_TITLE}
              tabs={MY_PROFILE_TABS}
              activeTab={activeTab}
              onChange={(_value, tab) => navigate(tab.path)}
            />

            <Box sx={{ minWidth: 0 }}>
              {activeTab === 'seguridad' ? (
                <ProfileSecuritySection onActionAlert={showActionAlert} />
              ) : loading ? (
                <Box sx={myProfilePanelLoaderSx}>
                  <LoadingSpinner text={MY_PROFILE_LOADING_TEXT} />
                </Box>
              ) : (
                <ProfileInfoSection
                  profile={profile}
                  onProfileChange={setProfile}
                  onActionAlert={showActionAlert}
                />
              )}
            </Box>
          </Box>
        </div>
      </div>
    </div>
  )
}
